/* eslint-disable no-empty */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import imageCompression from "browser-image-compression";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { S } from "@/pages/ibloom/styles";

const INTERVALS = [5, 15, 30];

const fmtTime = (iso) => {
  try { return new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; }
};

export default function ShelfSentry() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const wakeLockRef = useRef(null);
  const scanningRef = useRef(false);
  const seenNamesRef = useRef(new Set());
  const nextAtRef = useRef(null);

  const [running, setRunning] = useState(false);
  const [intervalMin, setIntervalMin] = useState(15);
  const [countdown, setCountdown] = useState("");
  const [scanning, setScanning] = useState(false);
  const [stats, setStats] = useState({ scans: 0, lastAt: null, lastDetected: 0, lastMatched: 0 });
  const [newItems, setNewItems] = useState([]);
  const [log, setLog] = useState([]);
  const [camError, setCamError] = useState("");
  const [geo, setGeo] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { toast("Sign in to use Shelf Sentry."); navigate("/login"); return; }
    api.get("/shelf/sentry/log").then((r) => setLog(r.data.scans || [])).catch(() => {});
    navigator.geolocation?.getCurrentPosition(
      (p) => setGeo({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => {},
      { enableHighAccuracy: true, timeout: 8000 }
    );
  }, [authLoading, user, navigate]);

  const acquireWakeLock = async () => {
    try {
      wakeLockRef.current = await navigator.wakeLock?.request("screen");
    } catch {}
  };

  useEffect(() => {
    const onVis = () => { if (running && document.visibilityState === "visible") acquireWakeLock(); };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [running]);

  const captureFrame = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return null;
    const scale = Math.min(1, 1600 / v.videoWidth);
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(v.videoWidth * scale);
    canvas.height = Math.round(v.videoHeight * scale);
    canvas.getContext("2d").drawImage(v, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/jpeg", 0.8);
  };

  const doScan = async () => {
    if (scanningRef.current) return;
    const frame = captureFrame();
    if (!frame) return;
    scanningRef.current = true;
    setScanning(true);
    try {
      const r = await api.post("/shelf/sentry/scan", { image_data: frame });
      const d = r.data;
      setStats((s) => ({ scans: s.scans + 1, lastAt: d.at, lastDetected: d.detected, lastMatched: d.matched }));
      setLog((l) => [{ id: d.scan_id, created_at: d.at, detected_count: d.detected, matched_count: d.matched, new_count: (d.new_items || []).length }, ...l].slice(0, 30));
      const fresh = (d.new_items || []).filter((it) => {
        const key = it.name.toLowerCase();
        if (seenNamesRef.current.has(key)) return false;
        seenNamesRef.current.add(key);
        return true;
      });
      if (fresh.length) {
        setNewItems((ni) => [...fresh.map((it, i) => ({ ...it, _id: Date.now() + i, price: it.price_guess != null ? String(it.price_guess) : "", status: "draft" })), ...ni]);
        toast(`🆕 Sentry spotted ${fresh.length} new item${fresh.length > 1 ? "s" : ""} on your shelf`);
      }
    } catch (e) {
      toast(e?.response?.data?.detail || "Sentry scan failed — will retry next cycle.");
    } finally {
      scanningRef.current = false;
      setScanning(false);
      nextAtRef.current = Date.now() + intervalMin * 60 * 1000;
    }
  };

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment", width: { ideal: 1920 } }, audio: false });
      streamRef.current = stream;
      setRunning(true);
      setCamError("");
      await acquireWakeLock();
      setTimeout(() => {
        if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.play().catch(() => {}); }
      }, 50);
      nextAtRef.current = Date.now() + 4000; // first scan ~4s after start
    } catch (e) {
      setCamError("Camera access denied. Allow camera permission and try again.");
    }
  };

  const stop = () => {
    setRunning(false);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    try { wakeLockRef.current?.release(); } catch {}
    wakeLockRef.current = null;
    nextAtRef.current = null;
    setCountdown("");
  };

  useEffect(() => () => stop(), []); // cleanup on unmount

  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => {
      if (!nextAtRef.current) return;
      const left = nextAtRef.current - Date.now();
      if (left <= 0 && !scanningRef.current) {
        doScan();
      } else {
        const s = Math.max(0, Math.floor(left / 1000));
        setCountdown(`${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`);
      }
    }, 1000);
    return () => clearInterval(t);
  }, [running, intervalMin]); // eslint-disable-line react-hooks/exhaustive-deps

  const setItem = (id, patch) => setNewItems((ni) => ni.map((x) => (x._id === id ? { ...x, ...patch } : x)));

  const replaceRef = useRef(null);
  const replaceTargetRef = useRef(null);
  const pickReplacement = (id) => { replaceTargetRef.current = id; replaceRef.current?.click(); };
  const onReplacePick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    const id = replaceTargetRef.current;
    if (!f || id == null) return;
    try {
      const compressed = await imageCompression(f, { maxSizeMB: 0.5, maxWidthOrHeight: 1024, initialQuality: 0.8, useWebWorker: true });
      const dataUrl = await imageCompression.getDataUrlFromFile(compressed);
      setItem(id, { crop_image_data: dataUrl });
      toast("📷 Photo replaced for this product.");
    } catch {
      toast("Couldn't read that photo — try again.");
    }
  };

  const publishItem = async (it) => {
    if (!it.price || Number(it.price) <= 0) { toast("Add a ₹ price first."); return; }
    setItem(it._id, { status: "publishing" });
    try {
      await api.post("/products/publish", {
        title: it.name,
        description: it.description || it.name,
        category: it.category,
        price: Number(it.price),
        original_image_data: it.crop_image_data,
        ai_image_pending: true,
        image_style: "studio",
        ...(geo ? { lat: geo.lat, lng: geo.lng } : {}),
      });
      setItem(it._id, { status: "live" });
      toast(`✅ "${it.name.slice(0, 40)}" is live`);
    } catch (e) {
      setItem(it._id, { status: "draft" });
      toast(e?.response?.data?.detail || "Publish failed");
    }
  };

  const card = { background: "white", border: "1px solid #EAE8DC", borderRadius: 16, boxShadow: "0 3px 10px rgba(15,20,30,0.05)" };

  return (
    <div data-testid="shelf-sentry-page" style={{ minHeight: "100dvh", background: "#0E1512", color: "white" }}>
      <div style={{ position: "sticky", top: 0, zIndex: 20, background: "rgba(14,21,18,0.92)", backdropFilter: "blur(10px)", borderBottom: "1px solid #1F2B25", padding: "12px 16px", display: "flex", alignItems: "center", gap: 12 }}>
        <button data-testid="sentry-back-btn" onClick={() => { stop(); navigate(-1); }} style={{ width: 34, height: 34, borderRadius: "50%", border: "1px solid #2A3A32", background: "#16211C", fontSize: 16, cursor: "pointer", color: "#9FE8C4", flexShrink: 0 }}>‹</button>
        <div style={{ ...S.fraunces, fontSize: 18, fontWeight: 800, color: "#9FE8C4" }} data-no-translate>🛰️ Shelf Sentry</div>
        {running && <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 10.5, fontWeight: 800, color: "#5EE39A" }}><span style={{ width: 7, height: 7, borderRadius: "50%", background: "#5EE39A", animation: "sentry-blink 1.4s infinite" }} />LIVE</span>}
        <style>{`@keyframes sentry-blink { 0%,100%{opacity:1} 50%{opacity:0.25} }`}</style>
      </div>

      <div style={{ maxWidth: 560, margin: "0 auto", padding: "18px 16px 60px" }}>
        <input ref={replaceRef} type="file" accept="image/*" capture="environment" onChange={onReplacePick} style={{ display: "none" }} data-testid="sentry-replace-input" />
        {!running ? (
          <>
            <div style={{ background: "linear-gradient(135deg, #14352A, #0F3D2E)", borderRadius: 20, padding: "24px 20px", border: "1px solid #1F4A3A" }}>
              <h1 style={{ ...S.fraunces, fontSize: 22, fontWeight: 800, lineHeight: 1.25, margin: 0, color: "white" }}>Mount a phone. Shelf watches itself.</h1>
              <p style={{ fontSize: 13, opacity: 0.85, lineHeight: 1.6, margin: "10px 0 0" }}>
                Place any spare phone facing your shelf, plug in the charger, and tap Start. MunimJi auto-scans the shelf, spots new items and keeps your online catalogue honest — hands-free.
              </p>
              <div style={{ marginTop: 18 }}>
                <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 1, opacity: 0.7, marginBottom: 8 }} data-no-translate>SCAN EVERY</div>
                <div style={{ display: "flex", gap: 8 }}>
                  {INTERVALS.map((m) => (
                    <button key={m} data-testid={`sentry-interval-${m}`} onClick={() => setIntervalMin(m)} style={{ flex: 1, padding: "10px 0", borderRadius: 100, border: intervalMin === m ? "none" : "1px solid #2A4A3C", background: intervalMin === m ? "#5EE39A" : "transparent", color: intervalMin === m ? "#0E1512" : "#9FE8C4", fontSize: 13, fontWeight: 800, cursor: "pointer" }}>
                      {m} min
                    </button>
                  ))}
                </div>
              </div>
              <button data-testid="sentry-start-btn" onClick={start} style={{ marginTop: 18, width: "100%", padding: 15, borderRadius: 100, border: "none", background: "#5EE39A", color: "#0E1512", fontSize: 15, fontWeight: 800, cursor: "pointer", boxShadow: "0 8px 24px rgba(94,227,154,0.25)" }}>
                ▶ Start Sentry
              </button>
              {camError && <div data-testid="sentry-cam-error" style={{ marginTop: 10, fontSize: 12, color: "#FF9A8A", fontWeight: 700 }}>{camError}</div>}
            </div>
            <div style={{ marginTop: 14, background: "#131E19", border: "1px solid #1F2B25", borderRadius: 16, padding: 16 }}>
              <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 1, color: "#6B8578", marginBottom: 8 }} data-no-translate>SETUP TIPS</div>
              {["Use a spare phone on a small stand, ~1 m from the shelf", "Keep the charger plugged in — screen stays awake", "Good steady light on the shelf, labels facing the camera"].map((t, i) => (
                <div key={i} style={{ fontSize: 12.5, color: "#A8BDB2", padding: "4px 0" }}>✓ {t}</div>
              ))}
            </div>
            <button data-testid="sentry-full-auto-link" onClick={() => navigate("/shelf-auto")} style={{ marginTop: 14, width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "15px 16px", borderRadius: 16, border: "1px solid #6B5A2A", background: "linear-gradient(135deg, #2A230E, #1A1508)", cursor: "pointer", textAlign: "left" }}>
              <span style={{ fontSize: 24 }}>📹</span>
              <span style={{ minWidth: 0, flex: 1 }}>
                <span style={{ display: "block", fontSize: 13.5, fontWeight: 800, color: "#FFD700" }}>Ai CCTV upload <span style={{ fontSize: 9, fontWeight: 900, background: "#C97B3C", color: "white", borderRadius: 100, padding: "2px 7px", verticalAlign: "middle", marginLeft: 4 }}>BETA</span></span>
                <span style={{ display: "block", fontSize: 11.5, color: "#A89A6B", marginTop: 2 }}>Point at the shelf — AI circles each product, locks on, then lists it automatically</span>
              </span>
              <span style={{ color: "#FFD700", fontWeight: 800 }}>›</span>
            </button>
          </>
        ) : (
          <>
            <div style={{ position: "relative", borderRadius: 20, overflow: "hidden", border: "1px solid #1F4A3A", background: "black" }}>
              <video ref={videoRef} data-testid="sentry-video" muted playsInline style={{ width: "100%", maxHeight: 320, objectFit: "cover", display: "block" }} />
              <div style={{ position: "absolute", left: 10, bottom: 10, display: "flex", gap: 8, alignItems: "center" }}>
                <span data-testid="sentry-countdown" style={{ background: "rgba(0,0,0,0.65)", backdropFilter: "blur(6px)", borderRadius: 100, padding: "6px 12px", fontSize: 12.5, fontWeight: 800, color: "#5EE39A" }}>
                  {scanning ? "🔍 Scanning…" : `Next scan ${countdown || "…"}`}
                </span>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <button data-testid="sentry-scan-now-btn" onClick={doScan} disabled={scanning} style={{ flex: 1, padding: 13, borderRadius: 100, border: "none", background: scanning ? "#2A4A3C" : "#5EE39A", color: scanning ? "#7FA491" : "#0E1512", fontSize: 13.5, fontWeight: 800, cursor: "pointer" }}>
                {scanning ? "Scanning…" : "📸 Scan now"}
              </button>
              <button data-testid="sentry-stop-btn" onClick={stop} style={{ flex: 1, padding: 13, borderRadius: 100, border: "1px solid #4A2A2A", background: "transparent", color: "#FF9A8A", fontSize: 13.5, fontWeight: 800, cursor: "pointer" }}>
                ■ Stop Sentry
              </button>
            </div>
            <div data-testid="sentry-stats" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginTop: 12 }}>
              {[
                { v: stats.scans, l: "Scans" },
                { v: stats.lastDetected, l: "Items seen" },
                { v: stats.lastMatched, l: "In catalogue" },
              ].map((x, i) => (
                <div key={i} style={{ background: "#131E19", border: "1px solid #1F2B25", borderRadius: 14, padding: "12px 8px", textAlign: "center" }}>
                  <div style={{ fontSize: 20, fontWeight: 800, color: "#5EE39A" }} data-no-translate>{x.v}</div>
                  <div style={{ fontSize: 10, color: "#6B8578", fontWeight: 700, marginTop: 2 }}>{x.l}</div>
                </div>
              ))}
            </div>
          </>
        )}

        {newItems.length > 0 && (
          <div data-testid="sentry-new-items" style={{ marginTop: 18 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1, color: "#E8C46B" }} data-no-translate>🆕 NEW ON YOUR SHELF</div>
              <button
                data-testid="sentry-whatsapp-summary"
                onClick={() => {
                  const lines = newItems.slice(0, 10).map((x) => `• ${x.name}${x.price ? ` — ₹${x.price}` : ""}`).join("\n");
                  const msg = `🛰️ Mapbazar Shelf Sentry alert\n${newItems.length} new item(s) spotted on your shelf:\n${lines}\n\nOpen Mapbazar to publish them.`;
                  window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
                }}
                style={{ border: "1px solid #2A4A3C", background: "#16211C", color: "#5EE39A", borderRadius: 100, padding: "5px 11px", fontSize: 10.5, fontWeight: 800, cursor: "pointer" }}
              >📲 WhatsApp summary</button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {newItems.map((it) => (
                <div key={it._id} data-testid={`sentry-new-item-${it._id}`} style={{ ...card, background: "#131E19", border: "1px solid #2A3A32", display: "flex", gap: 10, padding: 10, alignItems: "center" }}>
                  <div style={{ position: "relative", flexShrink: 0 }}>
                    <img src={it.crop_image_data} alt={it.name} style={{ width: 62, height: 62, objectFit: "contain", borderRadius: 10, background: "#0E1512", display: "block" }} />
                    {it.status === "draft" && (
                      <>
                        <button data-testid={`sentry-replace-${it._id}`} onClick={() => pickReplacement(it._id, "replace")} style={{ position: "absolute", right: -4, bottom: -4, width: 22, height: 22, borderRadius: "50%", border: "1px solid #2A4A3C", background: "#0F3D2E", color: "white", fontSize: 10, cursor: "pointer", lineHeight: 1 }}>📷</button>
                        <button data-testid={`sentry-addphoto-${it._id}`} onClick={() => pickReplacement(it._id, "add")} style={{ position: "absolute", left: -4, bottom: -4, width: 22, height: 22, borderRadius: "50%", border: "1px solid #6B5A2A", background: "#C97B3C", color: "white", fontSize: 11, fontWeight: 900, cursor: "pointer", lineHeight: 1 }}>＋</button>
                      </>
                    )}
                  </div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 800, color: "white", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.name}</div>
                    {it.seen_at && <div style={{ fontSize: 9.5, color: "#6B8578", fontWeight: 700, marginTop: 2 }} data-no-translate>🕐 Seen on shelf at {fmtTime(it.seen_at)}{it.extras && it.extras.length ? ` · +${it.extras.length} photo${it.extras.length > 1 ? "s" : ""}` : ""}</div>}
                    <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 6 }}>
                      <span style={{ fontSize: 13, fontWeight: 800, color: "#9FE8C4" }}>₹</span>
                      <input
                        data-testid={`sentry-price-${it._id}`}
                        type="number" min="1" placeholder="Price" value={it.price}
                        onChange={(e) => setItem(it._id, { price: e.target.value })}
                        disabled={it.status !== "draft"}
                        style={{ width: 80, padding: "6px 8px", borderRadius: 8, border: "1px solid #2A4A3C", fontSize: 12.5, fontWeight: 800, background: "#0E1512", color: "white" }}
                      />
                      {it.status === "live" ? (
                        <span style={{ fontSize: 11, fontWeight: 900, color: "#5EE39A" }}>● LIVE</span>
                      ) : (
                        <button data-testid={`sentry-publish-${it._id}`} onClick={() => publishItem(it)} disabled={it.status === "publishing"} style={{ padding: "7px 14px", borderRadius: 100, border: "none", background: "#E8C46B", color: "#0E1512", fontSize: 11.5, fontWeight: 800, cursor: "pointer" }}>
                          {it.status === "publishing" ? "…" : "🚀 Publish"}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {log.length > 0 && (
          <div data-testid="sentry-log" style={{ marginTop: 18 }}>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1, color: "#6B8578", marginBottom: 10 }} data-no-translate>SCAN LOG</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {log.slice(0, 10).map((s) => (
                <div key={s.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "#131E19", border: "1px solid #1F2B25", borderRadius: 12, padding: "9px 13px", fontSize: 12 }} data-no-translate>
                  <span style={{ color: "#A8BDB2", fontWeight: 700 }}>{fmtTime(s.created_at)}</span>
                  <span style={{ color: "#6B8578" }}>{s.detected_count} seen · {s.matched_count} matched{s.new_count ? <span style={{ color: "#E8C46B", fontWeight: 800 }}> · {s.new_count} new</span> : null}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
